import { useRef, useCallback, useState, useEffect } from 'react';

export function useAmbientAudio() {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const trackIndexRef = useRef(0);
  const tracks = ['/audio/ambient.m4a', '/audio/reveal.m4a'];
  const gainNodeRef = useRef(null);
  const audioCtxRef = useRef(null);

  const initAudioContext = useCallback(() => {
    if (!audioCtxRef.current) {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const gain = ctx.createGain();
      gain.gain.value = 0.15;
      gain.connect(ctx.destination);
      audioCtxRef.current = ctx;
      gainNodeRef.current = gain;
    }
  }, []);

  const playTrack = useCallback((idx) => {
    initAudioContext();
    const audio = new Audio(tracks[idx % tracks.length]);
    audio.crossOrigin = 'anonymous';

    // Connect through gain node for volume ducking
    try {
      const source = audioCtxRef.current.createMediaElementSource(audio);
      source.connect(gainNodeRef.current);
    } catch (e) {
      // Already connected or not supported
      audio.volume = 0.15;
    }

    audio.addEventListener('ended', () => {
      trackIndexRef.current = (trackIndexRef.current + 1) % tracks.length;
      if (playing) {
        playTrack(trackIndexRef.current);
      }
    });

    audio.play().catch(() => {});
    if (audioRef.current) {
      audioRef.current.pause();
    }
    audioRef.current = audio;
  }, [playing, initAudioContext, tracks]);

  const toggleAmbient = useCallback(() => {
    if (playing) {
      if (audioRef.current) audioRef.current.pause();
      setPlaying(false);
    } else {
      setPlaying(true);
      playTrack(trackIndexRef.current);
    }
  }, [playing, playTrack]);

  // When playing changes to true, start playback
  useEffect(() => {
    if (playing && !audioRef.current?.paused === false) {
      playTrack(trackIndexRef.current);
    }
  }, [playing]);

  // Duck volume during AI speech
  const duckVolume = useCallback(() => {
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.setTargetAtTime(0.03, audioCtxRef.current.currentTime, 0.3);
    }
    if (audioRef.current) audioRef.current.volume = 0.03;
  }, []);

  const restoreVolume = useCallback(() => {
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.setTargetAtTime(0.15, audioCtxRef.current.currentTime, 0.5);
    }
    if (audioRef.current) audioRef.current.volume = 0.15;
  }, []);

  return { playing, toggleAmbient, duckVolume, restoreVolume };
}
